import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { AlgorhythmMetadata } from '../../types/algorhythm.types';

interface AlgorhythmMetadataDisplayProps {
  metadata: AlgorhythmMetadata;
  title?: string;
  subtitle?: string;
}

/**
 * Read-only display component for Algorhythm metadata
 * Used in Composite asset edit pages to show aggregated metadata
 */
export const AlgorhythmMetadataDisplay: React.FC<AlgorhythmMetadataDisplayProps> = ({
  metadata,
  title = '🎯 Algorhythm Metadata',
  subtitle,
}) => {
  if (!metadata) {
    return null;
  }

  const energyLevelColor = {
    low: 'info',
    medium: 'primary',
    high: 'warning',
    extreme: 'error',
  }[metadata.energyLevel || 'medium'] as 'info' | 'primary' | 'warning' | 'error';

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <MusicNoteIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>

      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      <Grid container spacing={2}>
        {/* Performance Context */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Performance Context
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {metadata.performanceContext && metadata.performanceContext.length > 0 ? (
              metadata.performanceContext.map((context, index) => (
                <Chip
                  key={index}
                  label={context.replace(/_/g, ' ')}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Not specified
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Target Audience */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Target Audience
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {metadata.targetAudience && metadata.targetAudience.length > 0 ? (
              // Remove duplicates using Array.from instead of spread operator
              Array.from(new Set(metadata.targetAudience)).map((audience, index) => (
                <Chip
                  key={index}
                  label={audience.replace(/_/g, ' ')}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Not specified
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Cultural Context */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Cultural Context
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {metadata.culturalContext && metadata.culturalContext.length > 0 ? (
              metadata.culturalContext.map((context, index) => (
                <Chip
                  key={index}
                  label={context.replace(/_/g, ' ')}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Not specified
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Musical Style */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Musical Style
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {metadata.musicalStyle && metadata.musicalStyle.length > 0 ? (
              metadata.musicalStyle.map((style, index) => (
                <Chip
                  key={index}
                  label={style.replace(/_/g, ' ')}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Not specified
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Energy Level */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Energy Level
          </Typography>
          <Chip
            label={metadata.energyLevel ? metadata.energyLevel.toUpperCase() : 'NOT SPECIFIED'}
            color={energyLevelColor}
            size="medium"
            sx={{ fontWeight: 'bold' }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AlgorhythmMetadataDisplay;
